import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useIntl, useModel } from '@umijs/max';
import { Avatar, Divider, Spin } from 'antd';
import { createStyles } from 'antd-style';
import classNames from 'classnames';
import React from 'react';
import { outLogin } from '@/services/ant-design-pro/api';
import { reporter } from '@/utils/tracker';
import { getAppGradient, getAppInitial } from '@/utils/appAvatar';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.userNickname}</span>;
};

// 样式定义：包含用户头像下拉菜单的所有样式
const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      width: 280,
      backgroundColor: token.colorBgElevated,
      borderRadius: token.borderRadiusLG,
      boxShadow: token.boxShadowSecondary,
    },
    header: {
      padding: '20px 24px 12px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
    },
    name: {
      fontSize: 16,
      fontWeight: 600,
      color: token.colorTextHeading,
      lineHeight: 1.5,
    },
    account: {
      fontSize: 12,
      color: token.colorTextSecondary,
    },
    infoList: {
      padding: '0 24px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    infoItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: 12,
      color: token.colorTextSecondary,
    },
    infoLabel: {
      color: token.colorTextTertiary,
    },
    infoValue: {
      color: token.colorText,
      maxWidth: 160,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '12px 24px',
      cursor: 'pointer',
      color: token.colorText,
      transition: 'background-color 0.3s',
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
    logoutBtn: {
      color: token.colorError,
      justifyContent: 'center',
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    reporter.stopHeartbeat();
    try {
      const res = await outLogin();
      if (res.code === '200') {
        // 清空本地登录数据
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        localStorage.removeItem('user_info');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }

    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();
  const { formatMessage } = useIntl();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick = (key: string) => {
    if (key === 'logout') {
      loginOut().then(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser) {
    return loading;
  }

  return (
    <HeaderDropdown
      popupRender={() => (
        <div className={styles.container}>
          <div className={styles.header}>
            <Avatar
              size={64}
              src={currentUser?.avatar || undefined}
              style={currentUser?.avatar ? {} : {
                background: getAppGradient(currentUser?.userNickname || ''),
                color: '#fff',
                border: 'none',
                fontSize: 28,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {!currentUser?.avatar && getAppInitial(currentUser?.userNickname || '')}
            </Avatar>
            <div style={{ textAlign: 'center' }}>
              <div className={styles.name}>{currentUser?.userNickname}</div>
              <div className={styles.account}>
                {currentUser?.userAccount || '-'}
              </div>
            </div>
          </div>
          <div className={styles.infoList}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>{formatMessage({ id: 'pages.app.detail.company' })}</span>
              <span
                className={styles.infoValue}
                title={currentUser?.companyName}
              >
                {currentUser?.companyName || '-'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>{formatMessage({ id: 'pages.app.common.department' })}</span>
              <span
                className={styles.infoValue}
                title={currentUser?.departmentName}
              >
                {currentUser?.departmentName || '-'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Email</span>
              <span className={styles.infoValue} title={currentUser?.email}>
                {currentUser?.email || '-'}
              </span>
            </div>
          </div>
          <Divider style={{ margin: 0 }} />
          {menu && (
            <>
              <div
                className={styles.menuItem}
                onClick={() => onMenuClick('center')}
              >
                <UserOutlined />
                <span>{formatMessage({ id: 'menu.account.center' })}</span>
              </div>
              <div
                className={styles.menuItem}
                onClick={() => onMenuClick('settings')}
              >
                <SettingOutlined />
                <span>{formatMessage({ id: 'menu.account.settings' })}</span>
              </div>
              <Divider style={{ margin: 0 }} />
            </>
          )}
          <div
            className={classNames(styles.menuItem, styles.logoutBtn)}
            onClick={() => onMenuClick('logout')}
          >
            <LogoutOutlined />
            <span>{formatMessage({ id: 'menu.account.logout' })}</span>
          </div>
        </div>
      )}
    >
      {children}
    </HeaderDropdown>
  );
};
